import { Box, Stack, styled } from '@mui/material'
import ToggleButton from '@mui/material/ToggleButton'
import useFlagCondition from 'components/shared/useFlagCondition'
import React, { memo, useCallback, useRef, useState } from 'react'
import { useWindowSize } from 'react-use'
import { CellInfoGroup } from 'redux/slices'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useEventsManager from 'redux/slices/hooks/useEventsManager'
import { useNotificationSlice } from 'redux/slices/hooks/useNotificationSlice'
import useFooterHeight from 'utils/useFooterHeight'
import usePreFilter from '../prefilter/usePreFilter'
import createMorphotypeFromClusterFromDataFrame from '../utils/createMorphotypeFromCluster'
import { LegendColorPicker } from './LegendColorPicker'
import LegendItem from './LegendItem'
import { StaticPlotLegend } from './StaticPlotLegend'

interface StyledToggleButtonProps {
  bgcolor: string
}

export const StyledToggleButton = styled(ToggleButton)(({ bgcolor }: StyledToggleButtonProps) => ({
  borderRadius: 0,
  '&.Mui-disabled': {
    border: 0,
    cursor: 'not-allowed',
    pointerEvents: 'auto',
  },
  '&.Mui-selected, &.Mui-selected:hover, &:hover': {
    backgroundColor: bgcolor,
  },
  width: '100%',
  height: 50,
  backgroundColor: bgcolor,
  cursor: 'pointer',
}))

const Legend = ({ showPreFilter }: { showPreFilter?: boolean }): JSX.Element => {
  const eventsManager = useEventsManager()

  const topBoxRef = useRef<HTMLDivElement>(null)

  const {
    setSelectedCellInfoGroupData,
    selectedCellInfoGroup,
    cellVisualizations: { plotDisplayType, pinnedCells, selectedCellInfoGroupName },
    addPinnedMorphotype,
    setShowMorphotypes,
    setHighlightedLeidenCluster,
    dataFrame,
    prefilteredCellIdSet,
  } = useCellVisualizationsSlice()
  const { displayNotification } = useNotificationSlice()
  const { morphotypesToRemove, setMorphotypesToRemove } = usePreFilter()
  const { data, isContinuous } = selectedCellInfoGroup
  const isDensity = plotDisplayType === 'density'
  const isLeidenCluster = selectedCellInfoGroupName === 'leiden'

  const colorChangeCount = useRef(0)
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)

  const pickerEnabled = useFlagCondition('cellVisualizationsColorPickerEnabled')
  const footerHeight = useFooterHeight()
  const { height: windowHeight } = useWindowSize()
  const innerHeight =
    windowHeight - (topBoxRef.current?.getBoundingClientRect().top ?? 0) - footerHeight - 40

  const handleOnClick = (e: React.MouseEvent) => {
    setPopoverAnchor(e.currentTarget as HTMLElement)
  }

  const onPopoverClose = (selectedColor: string) => {
    // Only update colors if a color was actually selected
    if (selectedColor) {
      colorChangeCount.current += 1
      eventsManager.sendCustomColorEvent(colorChangeCount.current)

      const label = popoverAnchor?.getAttribute('data-label')

      setSelectedCellInfoGroupData(
        data.map((x) => (x.value === label ? { ...x, color: selectedColor } : x))
      )
    }

    setPopoverAnchor(null)
  }

  // Get the current color of the clicked legend item
  const getCurrentColor = () => {
    if (!popoverAnchor) return ''
    const label = popoverAnchor.getAttribute('data-label')
    const item = data.find((x) => x.value === label)
    return item?.color || ''
  }

  const onHide = (item: CellInfoGroup['data'][number], wasHidden: boolean) => {
    setSelectedCellInfoGroupData(
      data.map((x) => (x.value === item.value ? { ...x, isHidden: !wasHidden } : x))
    )
  }

  const handleHoverStart = useCallback(
    (clusterValue: string) => {
      setHighlightedLeidenCluster(clusterValue)
    },
    [setHighlightedLeidenCluster]
  )

  const handleHoverEnd = useCallback(() => {
    setHighlightedLeidenCluster(undefined)
  }, [setHighlightedLeidenCluster])

  const handleCreateMorphotype = useCallback(
    (clusterValue: string) => {
      // Determine which morphotype list to use based on mode
      const existingMorphotypes = showPreFilter ? (morphotypesToRemove || []) : (pinnedCells || [])

      if (!dataFrame) {
        displayNotification({
          message: `Failed to create morphotype from Leiden Cluster ${clusterValue}: Data not loaded`,
          type: 'error',
          showIcon: false,
          origin: { vertical: 'bottom', horizontal: 'center' },
          timeout: 5000,
        })
        return
      }

      const newMorphotype = createMorphotypeFromClusterFromDataFrame({
        clusterValue,
        dataFrame,
        existingMorphotypes,
        prefilteredCellIdSet,
      })

      if (!newMorphotype) {
        displayNotification({
          message: `Failed to create morphotype from Leiden Cluster ${clusterValue}`,
          type: 'error',
          showIcon: false,
          origin: { vertical: 'bottom', horizontal: 'center' },
          timeout: 5000,
        })
        return
      }

      // Add the morphotype to the appropriate store
      if (showPreFilter) {
        // In pre-filter mode, add to morphotypes to remove
        setMorphotypesToRemove([newMorphotype, ...(morphotypesToRemove || [])])
      } else {
        // In normal mode, add to pinned morphotypes
        addPinnedMorphotype(newMorphotype)
        // Show the morphotypes drawer
        setShowMorphotypes(true)
        // Track cluster selection analytics
        eventsManager.sendSelectClusterEvent(
          newMorphotype.cells.points?.length ?? 0,
          clusterValue,
          'legend'
        )
      }

      // Show notification to user
      const actionText = showPreFilter ? 'exclusion filter' : 'morphotype'
      displayNotification({
        message: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} created from Leiden Cluster ${clusterValue}`,
        type: 'success',
        showIcon: false,
        origin: { vertical: 'bottom', horizontal: 'center' },
        timeout: 5000,
      })
    },
    [
      dataFrame,
      prefilteredCellIdSet,
      pinnedCells,
      morphotypesToRemove,
      showPreFilter,
      addPinnedMorphotype,
      setMorphotypesToRemove,
      setShowMorphotypes,
      displayNotification,
      eventsManager,
    ]
  )

  return (
    <Box ref={topBoxRef}>
      <Stack spacing={2} sx={{ height: innerHeight, overflow: 'auto', px: 1 }}>
        {isContinuous || (isDensity && showPreFilter) ? (
          <StaticPlotLegend height={innerHeight} />
        ) : (
          data.map((item) => {
            // Check if this cluster is already in morphotypesToRemove (prefilter mode only)
            const isAlreadyAddedToFilter = showPreFilter && isLeidenCluster
              ? (morphotypesToRemove || []).some(
                  (morphotype) => morphotype.leidenCluster === item.value
                )
              : false

            return (
              <LegendItem
                key={item.value}
                bgColor={isDensity ? undefined : item.color}
                label={item.value}
                onClick={pickerEnabled ? handleOnClick : undefined}
                dataLabel={item.value}
                isHidden={item.isHidden}
                onHide={(wh) => onHide(item, wh)}
                readOnly={showPreFilter}
                showCreateMorphotype={isLeidenCluster}
                onCreateMorphotype={handleCreateMorphotype}
                onHoverStart={isLeidenCluster ? handleHoverStart : undefined}
                onHoverEnd={isLeidenCluster ? handleHoverEnd : undefined}
                isPreFilter={showPreFilter}
                isAlreadyAddedToFilter={isAlreadyAddedToFilter}
              />
            )
          })
        )}
      </Stack>
      <LegendColorPicker
        open={!!popoverAnchor}
        onClose={onPopoverClose}
        anchorEl={popoverAnchor}
        currentColor={getCurrentColor()}
      />
    </Box>
  )
}

export default memo(Legend)
